// Helpers for resolving keyword-cannibalization fixes.
//
// The backend canonical-write apply (_apply_keyword_cannibalization_fix) needs a
// human-chosen WINNER URL and refuses to guess. To make "Apply to WordPress"
// usable we (1) suggest a sensible default winner from the competing URLs and
// (2) detect the geo-page false-positive pattern (location landing pages that
// share a service phrase but target different cities) — for which canonicalizing
// is HARMFUL, so we route those to differentiate/dismiss instead of canonical.
//
// These are frontend fallbacks that work on existing audit runs. When the
// backend later enriches issue_details with `suggested_winner` / `is_geo_pattern`
// the card prefers those.

// US state abbreviations (+ DC). Used to recognize the common local-SEO naming
// pattern "<service>-<city>-<state>" (e.g. "concrete-contractor-garner-nc").
// A few codes ("in", "or", "me", "hi", "ok", "pa") are also English words; that
// only ever biases toward classifying a group as geo, which is the SAFE error
// (it routes to manual differentiate/review rather than an auto canonical).
const US_STATES = new Set([
	"al", "ak", "az", "ar", "ca", "co", "ct", "de", "fl", "ga", "hi", "id",
	"il", "in", "ia", "ks", "ky", "la", "me", "md", "ma", "mi", "mn", "ms",
	"mo", "mt", "ne", "nv", "nh", "nj", "nm", "ny", "nc", "nd", "oh", "ok",
	"or", "pa", "ri", "sc", "sd", "tn", "tx", "ut", "vt", "va", "wa", "wv",
	"wi", "wy", "dc",
]);

// A token that looks like a place: a word >=4 letters (city name) or a US state
// code. Numbers/dates ("13", "20260518") and short non-state tokens are NOT
// place-like, so numeric duplicates stay real cannibalization (winner picker).
function isPlaceLikeToken(t: string): boolean {
	return /^[a-z]{4,}$/.test(t) || US_STATES.has(t);
}

function lastSlug(url: string): string {
	let path = "";
	try {
		path = new URL(url).pathname;
	} catch {
		path = url;
	}
	const segs = path.split("/").filter(Boolean);
	return (segs[segs.length - 1] || "").toLowerCase();
}

function pathInfo(url: string): { dated: number; depth: number; len: number } {
	let path = "/";
	try {
		path = new URL(url).pathname;
	} catch {
		path = url;
	}
	// Dated blog permalinks (/YYYY/MM/.. ) make poor canonical winners.
	const dated = /\/\d{4}\/\d{2}\//.test(path) ? 1 : 0;
	const depth = path.split("/").filter(Boolean).length;
	return { dated, depth, len: path.length };
}

/**
 * Suggest the page that should "win" the keyword (the canonical target).
 * Heuristic, overridable by the user: prefer non-dated, shallower, shorter URLs.
 */
export function suggestWinner(urls: string[]): string | null {
	const clean = urls.filter(Boolean);
	if (clean.length === 0) return null;
	const scored = clean.map((u) => ({ u, ...pathInfo(u) }));
	scored.sort(
		(a, b) =>
			a.dated - b.dated ||
			a.depth - b.depth ||
			a.len - b.len ||
			a.u.localeCompare(b.u),
	);
	return scored[0].u;
}

/**
 * Conservative geo-pattern detection. Returns true only when the competing
 * pages are almost certainly location landing pages (same service phrase,
 * different city) — where a canonical merge would delete legitimate geo pages.
 *
 * High-precision by design: when unsure we return false (treat as a real
 * cannibalization group and show the winner picker — a human still confirms).
 */
export function isGeoCannibalization(urls: string[]): boolean {
	const slugs = urls.map(lastSlug).filter(Boolean);
	if (slugs.length < 2) return false;

	// Signal 1: every page uses a location preposition ("...-in-deltona",
	// "...-near-orlando", "...-serving-tampa").
	const prep = /(?:^|-)(?:in|near|serving)-[a-z]{3,}/;
	if (slugs.every((s) => prep.test(s))) return true;

	// Signal 2: every slug ends with a US state code ("...-garner-nc",
	// "...-fuquay-varina-nc") — a strong "<service>-<city>-<state>" geo pattern.
	const endsWithState = (s: string) => {
		const toks = s.split("-").filter(Boolean);
		return toks.length > 0 && US_STATES.has(toks[toks.length - 1]);
	};
	if (slugs.every(endsWithState)) return true;

	// Signal 3: identical token skeleton differing ONLY by place-like tokens
	// (city words >=4 letters or a US state code), distinct per page. Numeric/
	// date dupes ("fence-installation-13") differ by numbers, so they fall
	// through to the winner picker (correct — those ARE cannibalization).
	const tokenSets = slugs.map((s) => s.split("-").filter(Boolean));
	const common = tokenSets.reduce((acc, t) => acc.filter((x) => t.includes(x)));
	if (common.length < 2) return false;
	const diffs = tokenSets.map((t) => t.filter((x) => !common.includes(x)));
	const allPlace = diffs.every(
		(d) => d.length >= 1 && d.every(isPlaceLikeToken),
	);
	if (!allPlace) return false;
	const distinct = new Set(diffs.map((d) => d.join("-")));
	return distinct.size === slugs.length;
}

/**
 * Softer signal for a UI warning (defense-in-depth): the group is NOT confidently
 * geo, but at least one competing URL carries a location hint (state code or a
 * location preposition). Used to caution the reviewer before they canonicalize a
 * possibly-geo group that auto-detection didn't fully catch (e.g. a generic hub
 * page mixed with per-city pages).
 */
export function hasLocationSignals(urls: string[]): boolean {
	const slugs = urls.map(lastSlug).filter(Boolean);
	const prep = /(?:^|-)(?:in|near|serving)-[a-z]{3,}/;
	return slugs.some((s) => {
		if (prep.test(s)) return true;
		const toks = s.split("-").filter(Boolean);
		return toks.length > 1 && US_STATES.has(toks[toks.length - 1]);
	});
}

/** Compare two URLs ignoring a trailing slash / protocol-case differences. */
export function sameUrl(a: string, b: string): boolean {
	const norm = (u: string) => {
		try {
			const x = new URL(u);
			return (x.origin + x.pathname).replace(/\/+$/, "").toLowerCase();
		} catch {
			return (u || "").replace(/\/+$/, "").toLowerCase();
		}
	};
	return norm(a) === norm(b);
}
